import type { Store } from 'redux';
import type { Persistor } from 'redux-persist';
import {
    userManager,
    forceLogout,
    isHardAuthFailure,
    isLoggedOutLocked,
    subscribeAuthChannel,
    broadcastUserLoaded,
} from './AuthService';
import {
    setUserAuthInfo,
    LOGOUT,
} from '../store/redux/actions/userActionCreator';

// Bridge between oidc-client-ts UserManager events and Redux state, plus
// cross-tab sync via BroadcastChannel (with a storage-event fallback for
// environments where BroadcastChannel is unavailable).
//
// Must be called ONCE, synchronously, at app boot before any async work
// (e.g. i18n.loadNamespaces) — the 401 interceptor and PrivateRoute can
// trigger UserManager events the moment React starts rendering, and those
// events will be dropped on the floor if this hasn't run yet.

let setupComplete = false;

export function setupAuthLifecycle(store: Store, persistor: Persistor): void {
    if (setupComplete) return;
    setupComplete = true;

    // addUserLoaded fires after signinCallback / signinSilent completes
    // successfully. It does NOT fire from getUser() reads, so the boot-time
    // seed below is still needed for already-persisted sessions.
    userManager.events.addUserLoaded((user) => {
        if (isLoggedOutLocked()) return;
        store.dispatch(setUserAuthInfo(user));
        broadcastUserLoaded();
    });

    // addUserUnloaded fires from removeUser() (voluntary or forced logout).
    // Translate it into the Redux LOGOUT action so the root reducer resets
    // every slice. Guarded against repeat firing.
    userManager.events.addUserUnloaded(() => {
        store.dispatch({ type: LOGOUT });
    });

    // Only treat definitive "session is gone" errors as force-logout
    // triggers. Transient network errors during silent-renew should NOT
    // kick the user out — they'll get an opportunity to refresh on the
    // next 401 anyway, and force-logging them out on a flaky connection
    // is worse UX than letting the stale token ride. See isHardAuthFailure
    // in AuthService.ts for the exact classification rules.
    userManager.events.addSilentRenewError((err) => {
        if (isHardAuthFailure(err)) {
            const code = (err as { error?: string })?.error ?? 'unknown';
            forceLogout(`silent-renew-error:${code}`);
        } else {
            console.warn('[auth] silent renew failed (transient, not forcing logout):', err);
        }
    });

    // Cross-tab: another tab broadcast that it logged out. Don't call
    // forceLogout here — that would re-broadcast and could loop. Instead,
    // drop our local session and navigate to "/" so the landing page
    // renders without triggering a fresh IdP redirect.
    subscribeAuthChannel((msg) => {
        // Validate shape defensively — anything on the channel could be
        // posted by same-origin code, and `type` may not be a string if the
        // sender was buggy or malicious.
        if (!msg || typeof (msg as { type?: unknown }).type !== 'string') return;
        if (msg.type === 'logout') {
            void applyCrossTabLogout(store, persistor);
        } else if (msg.type === 'user-loaded') {
            // Another tab refreshed. Re-read OIDC storage and sync Redux so
            // the new access token is picked up here too.
            void userManager.getUser().then((u) => {
                if (u && !u.expired && !isLoggedOutLocked()) {
                    store.dispatch(setUserAuthInfo(u));
                }
            });
        }
    });

    // Cross-tab logout signal for browsers/modes without BroadcastChannel
    // (Safari private mode). `storage` events fire in OTHER tabs when
    // localStorage changes — NOT in the tab that made the change. So this
    // only serves as the *receive* path; the sending tab's own cleanup
    // still runs inline via forceLogout / logout thunk.
    try {
        window.addEventListener('storage', (e) => {
            if (e.key === 'persist:root' && e.newValue === null) {
                void applyCrossTabLogout(store, persistor);
            }
        });
    } catch {
        // storage events aren't available in some environments (e.g. SSR)
    }

    // Boot-time seed: getUser() reads from the OIDC userStore synchronously
    // (well, returns a Promise that resolves from the same tick). If there's
    // a valid persisted user, mirror it into Redux — redux-persist may not
    // have rehydrated yet, and the 401 interceptor's token fallbacks want
    // Redux to be authoritative once seeded.
    void userManager.getUser().then((u) => {
        if (u && !u.expired && !isLoggedOutLocked()) {
            store.dispatch(setUserAuthInfo(u));
        }
    });
}

async function applyCrossTabLogout(store: Store, persistor: Persistor): Promise<void> {
    if (isLoggedOutLocked()) return;
    if (!store.getState().user?.isLoggedIn) return;
    try {
        persistor.pause();
        await userManager.removeUser().catch(() => {});
        await persistor.purge().catch(() => {});
        persistor.persist();
        if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/') {
            window.location.assign('/');
        }
    } catch (e) {
        console.warn('[auth] cross-tab logout cleanup failed:', e);
    }
}
